import type { RouteRecordRaw } from 'vue-router';

/**
 * 路由表 — meta.perm 为菜单级权限码；meta.crumb 为面包屑标题。
 * P0 六屏：登录 / 中控看板 / 订单中心 / 订单详情 / 用户管理 / 角色权限。
 * P1、P2（监控报警、样本库、用户群传输、报表、结算、AI）在后续增量按同一约定接入。
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/LoginView.vue'),
    meta: { public: true, title: '登录' },
  },
  {
    path: '/',
    component: () => import('@/layouts/AdminLayout.vue'),
    children: [
      { path: '', redirect: '/dashboard' },
      {
        // t7（用户 m00036 第 3 条）：「真实产品维护」更名「产品管理」，并移入新增的「上游渠道」板块
        // （该板块的「渠道管理」入口见下方 channels 条目，b8 批已交付）。⛔ 路径仍留在 /masterdata/products——
        // RegionSourceView.vue 的链接与既有书签不得断链。
        path: 'masterdata/products', name: 'masterdata-products',
        component: () => import('@/views/masterdata/ProductMaintenanceView.vue'),
        meta: { title: '产品管理', crumb: '上游渠道 / 产品管理', perm: 'system', menu: true, menuOrder: 8.6, menuGroup: '上游渠道' },
      },
      {
        // b8 批（用户 m00036 第 3 条）：「上游渠道」板块下新增「渠道管理」子模块（md_channel 读两枚＋写两枚）。
        // 排在产品管理（8.6）之后、主数据维护板块（8.7）之前，仍属同一板块，权限码沿板块口径 system。
        path: 'channels', name: 'channels',
        component: () => import('@/views/channel/ChannelListView.vue'),
        meta: { title: '渠道管理', crumb: '上游渠道 / 渠道管理', perm: 'system', menu: true, menuOrder: 8.62, menuGroup: '上游渠道' },
      },
      {
        path: 'masterdata/sources', name: 'masterdata-sources',
        component: () => import('@/views/masterdata/RegionSourceView.vue'),
        meta: { title: '正式资料与区划映射', crumb: '主数据 / 资料审核', perm: 'system', menu: true, menuOrder: 8.7, menuGroup: '主数据维护' },
      },
      {
        path: 'masterdata/blacklist', name: 'masterdata-blacklist',
        component: () => import('@/views/masterdata/BlacklistListView.vue'),
        meta: { title: '黑名单管理', crumb: '主数据维护 / 黑名单管理', perm: 'system', menu: true, menuOrder: 8.72, menuGroup: '主数据维护' },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: { title: '中控看板', crumb: '中控看板', perm: 'dashboard:view', menu: true, menuOrder: 1 },
      },
      {
        // 看板漏斗标题右侧「节点下钻 →」的落点（TC-E02）：详情屏不进导航，高亮仍落在看板入口；
        // 周期由 `?period=` 带过来（缺省 today），权限与看板同一枚 `dashboard:view`。
        path: 'funnel',
        name: 'funnel-drill',
        component: () => import('@/views/dashboard/FunnelDrillView.vue'),
        meta: { title: '漏斗节点下钻', crumb: '中控看板 / 节点下钻', perm: 'dashboard:view', activeMenu: 'dashboard' },
      },
      {
        path: 'orders',
        name: 'orders',
        component: () => import('@/views/orders/OrderListView.vue'),
        meta: { title: '订单中心', crumb: '订单中心', perm: 'order:view', menu: true, menuOrder: 2 },
      },
      {
        path: 'orders/:id',
        name: 'order-detail',
        component: () => import('@/views/orders/OrderDetailView.vue'),
        meta: { title: '订单详情', crumb: '订单详情', perm: 'order:view', activeMenu: 'orders' },
      },
      {
        // 群主 seq=854：在既有管理端壳内做增量——分组「BPO 机构管理」只有机构列表与角色配置两枚入口
        path: 'bpo-agencies',
        name: 'agency-list',
        component: () => import('@/views/agency/AgencyListView.vue'),
        meta: {
          title: '机构列表',
          crumb: 'BPO 机构管理 / 机构列表',
          perm: 'agency:view',
          menu: true,
          menuOrder: 9,
          menuGroup: 'BPO 机构管理',
        },
      },
      {
        // 项负责 seq=866：详情屏不进导航，从列表行点进；高亮仍落在列表入口
        path: 'bpo-agencies/:id',
        name: 'agency-detail',
        component: () => import('@/views/agency/AgencyDetailView.vue'),
        meta: { title: '机构详情', crumb: 'BPO 机构管理 / 机构详情', perm: 'agency:view', activeMenu: 'agency-list' },
      },
      {
        path: 'bpo-role-templates',
        name: 'agency-roles',
        component: () => import('@/views/agency/AgencyRoleView.vue'),
        meta: {
          title: '角色配置',
          crumb: 'BPO 机构管理 / 角色配置',
          perm: 'agency:view',
          menu: true,
          menuOrder: 9.5,
          menuGroup: 'BPO 机构管理',
        },
      },
      {
        // t9（用户 m00149 第 2 条）：录单员管理＝全平台列表，不是按机构；数据腿 GET /bpo-agencies/members
        path: 'bpo-staff/clerks',
        name: 'agency-clerks',
        component: () => import('@/views/agency/AgencyStaffListView.vue'),
        meta: {
          title: '录单员管理',
          crumb: 'BPO 机构管理 / 录单员管理',
          perm: 'agency:view',
          menu: true,
          menuOrder: 9.2,
          menuGroup: 'BPO 机构管理',
          agency: 'bpo',
          memberRole: 'AGENT',
        },
      },
      {
        // t9（用户 m00149 第 2 条）：BPO管理员管理＝全平台列表
        path: 'bpo-staff/admins',
        name: 'agency-admins',
        component: () => import('@/views/agency/AgencyStaffListView.vue'),
        meta: {
          title: 'BPO管理员管理',
          crumb: 'BPO 机构管理 / BPO管理员管理',
          perm: 'agency:view',
          menu: true,
          menuOrder: 9.4,
          menuGroup: 'BPO 机构管理',
          agency: 'bpo',
          memberRole: 'ADMIN',
        },
      },
      {
        // f3（2026-10-19 生产测试反馈批）：BPO 机构管理下的「外呼记录管理」——列表读权沿用外呼记录读权
        // （audience，与数源管理同码），导入另用 audience:import；数据腿 GET/POST /outbound-calls。
        path: 'bpo-outbound-calls',
        name: 'agency-outbound-calls',
        component: () => import('@/views/agency/OutboundCallListView.vue'),
        meta: {
          title: '外呼记录管理',
          crumb: 'BPO 机构管理 / 外呼记录管理',
          perm: 'audience',
          menu: true,
          menuOrder: 9.6,
          menuGroup: 'BPO 机构管理',
        },
      },
      // ── 交付机构管理（契约 §6.1；裁定增补 R-1/R-2；t9 全平台列表改造）───────────────
      // 分组「交付机构管理」7 条路由：列表（导航）／详情（不进导航，行内点进）／
      // 交付员管理·交付管理员管理（**平台全平台列表**，进导航：t9 用户 m00149 第 3 条，
      // 数据腿 GET /delivery-agencies/members；⛔ 不再要求先选机构）／
      // 同两类的机构内视图（路径带 `:id`，**不进导航**，从机构详情点进，保留按机构查看成员的原腿）／
      // 角色配置。
      {
        path: 'delivery-agencies',
        name: 'delivery-agency-list',
        component: () => import('@/views/delivery-agency/DeliveryAgencyListView.vue'),
        meta: {
          title: '交付机构列表',
          crumb: '交付机构管理 / 交付机构列表',
          perm: 'dagency:view',
          menu: true,
          menuOrder: 12,
          menuGroup: '交付机构管理',
        },
      },
      {
        path: 'delivery-agencies/:id',
        name: 'delivery-agency-detail',
        component: () => import('@/views/delivery-agency/DeliveryAgencyDetailView.vue'),
        meta: {
          title: '交付机构详情',
          crumb: '交付机构管理 / 交付机构详情',
          perm: 'dagency:view',
          activeMenu: 'delivery-agency-list',
        },
      },
      {
        // t9（用户 m00149 第 3 条）：侧栏入口不再带 :id ⇒ 点进即全平台交付员列表
        path: 'delivery-staff/couriers',
        name: 'delivery-staff-couriers',
        component: () => import('@/views/agency/AgencyStaffListView.vue'),
        meta: {
          title: '交付员管理',
          crumb: '交付机构管理 / 交付员管理',
          perm: 'dagency:staff',
          menu: true,
          menuOrder: 12.2,
          menuGroup: '交付机构管理',
          agency: 'delivery',
          memberRole: 'COURIER',
        },
      },
      {
        // t9（用户 m00149 第 3 条）：同上，交付管理员口径
        path: 'delivery-staff/admins',
        name: 'delivery-staff-admins',
        component: () => import('@/views/agency/AgencyStaffListView.vue'),
        meta: {
          title: '交付管理员管理',
          crumb: '交付机构管理 / 交付管理员管理',
          perm: 'dagency:staff',
          menu: true,
          menuOrder: 12.4,
          menuGroup: '交付机构管理',
          agency: 'delivery',
          memberRole: 'AGENCY_ADMIN',
        },
      },
      {
        // t9：机构内视图（带 :id）——⛔ 不进导航（侧栏直点没有参数），从机构详情点进；
        // 平台级同名入口见上方 delivery-staff/couriers，高亮落在那一枚。
        path: 'delivery-agencies/:id/couriers',
        name: 'delivery-agency-couriers',
        component: () => import('@/views/delivery-agency/DeliveryAgencyMemberSlotView.vue'),
        meta: {
          title: '交付员管理',
          crumb: '交付机构管理 / 交付员管理',
          perm: 'dagency:staff',
          activeMenu: 'delivery-staff-couriers',
          memberRole: 'COURIER',
        },
      },
      {
        // t9：机构内视图（带 :id）——同上，不进导航
        path: 'delivery-agencies/:id/admins',
        name: 'delivery-agency-admins',
        component: () => import('@/views/delivery-agency/DeliveryAgencyMemberSlotView.vue'),
        meta: {
          title: '交付管理员管理',
          crumb: '交付机构管理 / 交付管理员管理',
          perm: 'dagency:staff',
          activeMenu: 'delivery-staff-admins',
          memberRole: 'AGENCY_ADMIN',
        },
      },
      {
        path: 'delivery-role-templates',
        name: 'delivery-agency-roles',
        component: () => import('@/views/delivery-agency/DeliveryAgencyRoleView.vue'),
        meta: {
          title: '交付机构角色配置',
          crumb: '交付机构管理 / 交付机构角色配置',
          // 裁定 R-1 Δ2：用**节点码**（不是 dagency:view）⇒ 交付机构管理员侧该屏不渲染、直连 URL 无权限态
          perm: 'menu:delivery_agency_roles',
          menu: true,
          menuOrder: 12.6,
          menuGroup: '交付机构管理',
        },
      },
      {
        // t7（用户 m00036 第 1 条）：用户管理与角色权限上移到主数据维护（及新增「上游渠道」）板块之上，
        // 即紧接「报表中心」（menuOrder 6，原为 AI 8；2026-10-17 18:4x 追加后 AI 已上移到 5.6）之后；菜单级权限码不动。
        // t48（用户 03(2) 第 4 条）：两者收进独立一级分组「平台权限管理」——侧栏渲独立组标题，
        // ⛔ 不再平铺；组内按 menuOrder 8.3／8.4 定序，BPO 机构管理分组不受影响（仍四项）。
        path: 'users',
        name: 'users',
        component: () => import('@/views/users/UserListView.vue'),
        meta: {
          title: '用户管理',
          crumb: '用户管理',
          perm: 'user:view',
          menu: true,
          menuOrder: 8.3,
          menuGroup: '平台权限管理',
        },
      },
      {
        path: 'roles',
        name: 'roles',
        component: () => import('@/views/roles/RolePermissionView.vue'),
        meta: {
          title: '角色权限',
          crumb: '角色权限',
          perm: 'system',
          menu: true,
          menuOrder: 8.4,
          menuGroup: '平台权限管理',
        },
      },
      {
        path: 'alerts',
        name: 'alerts',
        component: () => import('@/views/alerts/AlertCenterView.vue'),
        meta: { title: '告警中心', crumb: '告警中心', perm: 'alert:view', menu: true, menuOrder: 3 },
      },
      {
        path: 'samples',
        name: 'samples',
        component: () => import('@/views/samples/SampleLibraryView.vue'),
        meta: { title: '样本库', crumb: '样本库', perm: 'sample', menu: true, menuOrder: 4 },
      },
      {
        path: 'audience',
        name: 'audience',
        component: () => import('@/views/audience/AudienceTransferView.vue'),
        meta: { title: '数源管理', crumb: '数源管理', perm: 'audience', menu: true, menuOrder: 5 },
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/reports/ReportsCenterView.vue'),
        // 反馈_09 追加（2026-10-17 用户 15:37）：侧栏新增一级栏目「报表中心」，本模块随之改名「自定义报表」；
        // 路由 name／path／权限码 report 一律不动，只动导航分组与显示名。
        meta: { title: '自定义报表', crumb: '报表中心 / 自定义报表', perm: 'report', menu: true, menuOrder: 6, menuGroup: '报表中心' },
      },
      {
        path: 'reports/roi-acquisition',
        name: 'reportsRoi',
        component: () => import('@/views/reports/RoiAcquisitionReportView.vue'),
        meta: { title: '外呼获客 ROI 报表', crumb: '报表中心 / 外呼获客 ROI 报表', perm: 'report', menu: true, menuOrder: 6.1, menuGroup: '报表中心' },
      },
      {
        // 用户 2026-10-17 18:4x：结算中心上移到「报表中心」一级栏目之上（紧邻其上的平铺入口之一）。
        // 与 AI 建议与分析同为平铺模块（⛔ 不塞进分组），只动导航位次 menuOrder；
        // path／name／权限码 menu:settlement_bills 与页面一律不动。
        path: 'settlement',
        name: 'settlement',
        component: () => import('@/views/settlement/SettlementCenterView.vue'),
        meta: { title: '结算中心', crumb: '结算中心', perm: 'menu:settlement_bills', menu: true, menuOrder: 5.4 },
      },
      {
        // 同一条用户追加：AI 建议与分析紧随结算中心，同样落在「报表中心」之上；path／name／权限码 ai 不动。
        path: 'ai',
        name: 'ai',
        component: () => import('@/views/ai/AiAnalysisView.vue'),
        meta: { title: 'AI 建议与分析', crumb: 'AI 建议与分析', perm: 'ai', menu: true, menuOrder: 5.6 },
      },
      {
        // 用户 2026-10-19：后台没有「给自己改密」的入口，超管只能走控制台脚本换口令；
        // 本页补上自助改密。纯自助、登录即可用，故不设权限码、也不进侧栏（meta.menu 缺省）。
        path: 'account/security',
        name: 'account-security',
        component: () => import('@/views/account/AccountSecurityView.vue'),
        meta: { title: '修改密码', crumb: '账号安全 / 修改密码' },
      },
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/system/ForbiddenView.vue'),
        meta: { title: '无访问权限', public: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/system/NotFoundView.vue'),
    meta: { public: true, title: '页面不存在' },
  },
];
